import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Spinner, Form, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faInfoCircle } from '@fortawesome/free-solid-svg-icons'; 
import '../../styles/DashboardFinanceiro.css';

// Componente para os cards de métricas principaisfa
const MetricCard = ({ title, value, percentage, formatCurrency, tooltipText }) => (
    <Card className="metric-card-modern text-center h-100">
        <Card.Body className="d-flex flex-column justify-content-center py-3">
            <div className="d-flex justify-content-center align-items-center mb-1">
                <Card.Title as="h6" className="mb-0 me-1">{title}</Card.Title>
                <OverlayTrigger
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    overlay={<Tooltip id={`tooltip-${title.replace(/\s/g, '')}`}>{tooltipText}</Tooltip>}
                >
                    <span className="info-icon-trigger">
                        <FontAwesomeIcon icon={faInfoCircle} />
                    </span>
                </OverlayTrigger>
            </div>
            <p className="metric-value mb-0">{formatCurrency(value)}</p>
            {percentage !== null && typeof percentage === 'number' && (
                <p className={`metric-percentage mb-0 ${percentage >= 0 ? 'text-success' : 'text-danger'}`}>
                    {percentage.toFixed(2)}%
                </p>
            )}
        </Card.Body>
    </Card>
);

// NOVO: Componente para os cards de despesas com visual moderno
const DetailedMetricCard = ({ title, data, formatCurrency, tooltipText }) => {
    // Fallback para o caso de os dados ainda não serem um objeto
    if (typeof data !== 'object' || data === null) {
        return (
            <Card className="metric-card-modern h-100">
                <Card.Body className="d-flex flex-column justify-content-center">
                    <div className="d-flex justify-content-start align-items-center">
                        <Card.Title as="h6" className="mb-0 me-1">{title}</Card.Title>
                        <OverlayTrigger placement="top" delay={{ show: 250, hide: 400 }} overlay={<Tooltip id={`tooltip-detailed-fallback-${title.replace(/\s/g, '')}`}>{tooltipText}</Tooltip>}>
                            <span className="info-icon-trigger">
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </span>
                        </OverlayTrigger>
                    </div>
                    <p className="metric-value">{formatCurrency(data)}</p>
                </Card.Body>
            </Card>
        );
    }

    const { valor, percentual_mes, percentual_ano } = data;
    const isPositive = percentual_mes >= 0;

    return (
        <Card className="metric-card-modern h-100">
            <Card.Body className="d-flex flex-column">
                <div className="d-flex justify-content-start align-items-center mb-2">
                    <Card.Title as="h6" className="mb-0 me-1">{title}</Card.Title>
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={<Tooltip id={`tooltip-detailed-${title.replace(/\s/g, '')}`}>{tooltipText}</Tooltip>}
                    >
                        <span className="info-icon-trigger">
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </OverlayTrigger>
                </div>
                <p className="metric-value mb-2">{formatCurrency(valor)}</p>
                <div className="mt-auto d-flex justify-content-between align-items-center metric-comparison">
                    <span className={`fw-bold ${isPositive ? 'text-primary' : 'text-danger'}`}>
                        <FontAwesomeIcon icon={isPositive ? faArrowUp : faArrowDown} className="me-1" />
                        {percentual_mes?.toFixed(2)}% Mês
                    </span>
                    <span className="text-muted">
                        {percentual_ano?.toFixed(2)}% Ano
                    </span>
                </div>
            </Card.Body>
        </Card>
    );
};

// Mapeamento dos textos para os tooltips
const tooltipTexts = {
    faturamento_hoje: "Soma de todos os pagamentos confirmados (pacientes e convênios) no dia de hoje.",
    faturamento_bruto: "Soma de todos os pagamentos confirmados (pacientes e convênios) no período selecionado.",    lucro_bruto: "Faturamento menos os custos diretos (Despesas Variáveis).",
    margem_contribuicao: "Valor que sobra do Faturamento Bruto para pagar despesas fixas (Faturamento - Custos e Deduções). ",
    ebitda: "Lucro antes de juros, impostos, depreciação e amortização (geração de caixa operacional). (Faturamento - Despesas Fixas e Variáveis)).",
    resultado_liquido: "Lucro final após todas as despesas e impostos (Ebitda - Impostos e Desp. não Operacionais). ",
    liquido_anual: "Soma do Resultado Líquido dos últimos 12 meses, incluindo o mês atual. Mostra o lucro acumulado no ano.",
    despesas_variaveis: "Gastos que variam conforme o volume de atendimentos, como comissões de profissionais e materiais diretos.",
    despesas_fixas: "Gastos recorrentes que não mudam com o volume de atendimentos, como aluguel, salários administrativos e contas de consumo.",
    custos_e_deducoes: "Custos diretos ligados à prestação do serviço e deduções sobre o faturamento, como impostos sobre serviços (ISS).",
    impostos_desp_nao_operacionais: "Impostos sobre o lucro (IRPJ, CSLL) e outras despesas que não estão diretamente ligadas à operação principal da clínica."
};

const DashboardFinanceiroTab = ({ isActive, currentEntidade, formatCurrency }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filtro de data (mês e ano)
  const [filterDate, setFilterDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!currentEntidade || !filterDate) return;
      setIsLoading(true);
      setError(null);
      try {
        // Endpoint que retorna todos os dados necessários para o novo dashboard
        const response = await axios.get(`/api/dashboard/financeiro/completo`, {
            params: {
                entidade_id: currentEntidade.id,
                periodo: filterDate // Envia 'YYYY-MM'
            }
        });
        setDashboardData(response.data);
      } catch (error) {
        console.error('Erro ao buscar dados do dashboard financeiro:', error);
        setError('Não foi possível carregar os dados do dashboard.');
        setDashboardData(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (isActive && currentEntidade) {
      fetchData();
    }
  }, [isActive, currentEntidade, filterDate]);

  // Função para truncar labels longos dos gráficos
  const truncateLabel = (label, maxLength = 20) => {
    if (label.length > maxLength) {
        return label.substring(0, maxLength) + '...';
    }
    return label;
  };

  // Memos para preparar os dados dos gráficos
  const faturamentoCategoriaChart = useMemo(() => {
    const data = dashboardData?.faturamento_por_categoria;
    if (!data) return { labels: [], datasets: [] };
    return {
        labels: data.labels.map(label => truncateLabel(label)),
        datasets: [{ label: 'Faturamento', data: data.values, backgroundColor: 'rgba(54, 162, 235, 0.6)' }]
    };
  }, [dashboardData]);

  const faturamentoDiarioChart = useMemo(() => {
    const data = dashboardData?.faturamento_diario_mes;
    if (!data || !data.labels || data.labels.length === 0) return { labels: [], datasets: [] };
    return {
        labels: data.labels,
        datasets: [{ label: 'Faturamento Diário', data: data.values, backgroundColor: 'rgba(153, 102, 255, 0.6)' }]
    };
  }, [dashboardData]);

  const historicoFaturamentoChart = useMemo(() => {
    const data = dashboardData?.historico_faturamento_bruto;
    if (!data) return { labels: [], datasets: [] };
    return {
        labels: data.labels,
        datasets: [{ label: 'Faturamento Bruto', data: data.values, backgroundColor: 'rgba(75, 192, 192, 0.6)' }]
    };
  }, [dashboardData]);

  const despesasCategoriaChart = useMemo(() => {
    const data = dashboardData?.despesas_por_categoria;
    if (!data) return { labels: [], datasets: [] };
    return {
        labels: data.labels.map(label => truncateLabel(label)),
        datasets: [{ label: 'Despesas', data: data.values, backgroundColor: 'rgba(255, 99, 132, 0.6)' }]
    };
  }, [dashboardData]);

  const chartOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, title: { display: true, text: title } },
    scales: { y: { beginAtZero: true, ticks: { callback: (value) => formatCurrency(value) } } }
  });

  if (isLoading) {
    return (
      <div className="text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
      return <Alert variant="danger">{error}</Alert>;
  }

  if (!dashboardData) {
      return (
        <>
            <Form.Group as={Row} className="mb-3 align-items-center">
                <Form.Label column sm="auto">Período:</Form.Label>
                <Col sm={3}>
                    <Form.Control type="month" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
                </Col>
            </Form.Group>
            <div className="text-center">Nenhum dado disponível para o período selecionado.</div>
        </>
      );
  }

  return (
    <>
        <Form.Group as={Row} className="mb-4 align-items-center">
            <Form.Label column sm="auto">Período:</Form.Label>
            <Col sm={3}>
                <Form.Control type="month" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
            </Col>
        </Form.Group>

      {/* Primeira Linha de Métricas */}
      <Row className="g-3 mb-4">
        <Col><MetricCard title="Faturamento Bruto" value={dashboardData.faturamento_bruto?.valor} percentage={null} formatCurrency={formatCurrency} tooltipText={tooltipTexts.faturamento_bruto} /></Col>
        <Col><MetricCard title="Lucro Bruto" value={dashboardData.lucro_bruto?.valor} percentage={dashboardData.lucro_bruto?.percentual} formatCurrency={formatCurrency} tooltipText={tooltipTexts.lucro_bruto} /></Col>
        <Col><MetricCard title="Margem Contrib." value={dashboardData.margem_contribuicao?.valor} percentage={dashboardData.margem_contribuicao?.percentual} formatCurrency={formatCurrency} tooltipText={tooltipTexts.margem_contribuicao} /></Col>
        <Col><MetricCard title="EBITDA" value={dashboardData.ebitda?.valor} percentage={dashboardData.ebitda?.percentual} formatCurrency={formatCurrency} tooltipText={tooltipTexts.ebitda} /></Col>
        <Col><MetricCard title="Resultado Líquido" value={dashboardData.resultado_liquido?.valor} percentage={dashboardData.resultado_liquido?.percentual} formatCurrency={formatCurrency} tooltipText={tooltipTexts.resultado_liquido} /></Col>
        <Col><MetricCard title="Líquido Anual" value={dashboardData.liquido_anual?.valor} percentage={dashboardData.liquido_anual?.percentual} formatCurrency={formatCurrency} tooltipText={tooltipTexts.liquido_anual} /></Col>
      </Row>

      {/* Segunda Linha com Duas Colunas */}
      <Row>
        {/* Coluna da Esquerda */}
        <Col lg={6}>
            <Row className="g-3">
                <Col md={6}><DetailedMetricCard title="Despesas Variáveis" data={dashboardData.despesas_variaveis} formatCurrency={formatCurrency} tooltipText={tooltipTexts.despesas_variaveis} /></Col>
                <Col md={6}><DetailedMetricCard title="Despesas Fixas (Saídas Operacionais)" data={dashboardData.despesas_fixas} formatCurrency={formatCurrency} tooltipText={tooltipTexts.despesas_fixas} /></Col>
                <Col md={6}><DetailedMetricCard title="Custos e Deduções" data={dashboardData.custos_e_deducoes} formatCurrency={formatCurrency} tooltipText={tooltipTexts.custos_e_deducoes} /></Col>
                <Col md={6}><DetailedMetricCard title="Impostos e Desp. não Operacionais" data={dashboardData.impostos_desp_nao_operacionais} formatCurrency={formatCurrency} tooltipText={tooltipTexts.impostos_desp_nao_operacionais} /></Col>
                <Col md={12}>
                    <Card className="metric-card-modern">
                        <Card.Body>
                            <div style={{ height: '254px' }}>
                                <Bar data={despesasCategoriaChart} options={chartOptions('Despesas por Categoria')} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Col>

        {/* Coluna da Direita */}
        <Col lg={6}>
            <Row className="g-3">
                <Col md={12}>
                    <Card className="metric-card-modern">
                        <Card.Body>
                            <div style={{ height: '135px' }}>
                                <Bar data={historicoFaturamentoChart} options={chartOptions('Histórico de Faturamento Bruto')} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={12}>
                    <Card className="metric-card-modern">
                        <Card.Body>
                            <div style={{ height: '135px' }}>
                                <Bar data={faturamentoDiarioChart} options={chartOptions('Faturamento Diário (Mês)')} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={12}>
                    <Card className="metric-card-modern">
                        <Card.Body>
                            <div style={{ height: '135px' }}>
                                <Bar data={faturamentoCategoriaChart} options={chartOptions('Faturamento por Serviços')} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Col>
      </Row>
    </>
  );
};

export default DashboardFinanceiroTab;